import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { apiRequest } from "@/lib/queryClient";
import { KeyRound, ArrowLeft, Loader2 } from "lucide-react";

interface ResetCodeVerificationProps {
  email: string;
  onBack: () => void;
  onCodeVerified: (resetToken: string) => void;
}

export function ResetCodeVerification({ email, onBack, onCodeVerified }: ResetCodeVerificationProps) {
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!code) {
      setError("Please enter the verification code");
      return;
    }

    if (code.length !== 6) {
      setError("The code must contain 6 digits");
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiRequest("POST", "/api/auth/verify-reset-code", {
        email,
        code: code.trim(),
      });

      const data = await response.json();

      if (data.success && data.resetToken) {
        onCodeVerified(data.resetToken);
      } else {
        setError(data.message || "Invalid or expired code");
      }
    } catch (err: any) {
      console.error("Verify code error:", err);
      setError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (value: string) => {
    // Only allow numbers and limit to 6 digits
    const numericValue = value.replace(/\D/g, "").slice(0, 6);
    setCode(numericValue);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl">Verification Code</CardTitle>
        <CardDescription>
          Enter the 6-digit code sent to {email}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="code">Verification Code</Label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="code"
                type="text"
                autoComplete="one-time-code"
                inputMode="numeric"
                placeholder="123456"
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                className="pl-10 text-center text-2xl tracking-widest font-mono"
                disabled={isLoading}
                maxLength={6}
                autoFocus
              />
            </div>
            <p className="text-xs text-gray-500 text-center">
              The code expires in 10 minutes
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col gap-2">
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || code.length !== 6}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify Code"
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={onBack}
              disabled={isLoading}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </div>

          <div className="text-center text-sm text-gray-600">
            Didn't receive the code?{" "}
            <button
              type="button"
              onClick={onBack}
              className="text-blue-600 hover:underline"
              disabled={isLoading}
            >
              Resend
            </button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

